<script lang="ts" setup>
import { computed } from "vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import PartnerStats from "./components/PartnerStats.vue";
import PartnerClientsList from "./components/PartnerClientsList.vue";
import PartnerActivity from "./components/PartnerActivity.vue";
import IssuesList from "./components/IssuesList.vue";
import PageHeader from "@/components/PageHeader.vue";
import { usePartnerActivities, usePartnerActivityCount } from "@/queries/activities";
import { usePartnerIssues, usePartnerIssueCount } from "@/queries/partner-issues";
import { usePartnerClientSummaries, rollupClientSummaries } from "@/queries/partner-clients";
import type { PartnerStats as PartnerStatsType } from "@/types/project-types";

// Live activity feed for the dashboard widget + live count for the stats card.
const { activities: recentActivity, activitiesLoading: activityWidgetLoading } =
  usePartnerActivities({ take: 5 });
const { count: recentActivityCount } = usePartnerActivityCount();

// Live high/critical open issues for the dashboard "Issues" card + the open-issue badge count.
const { issues: criticalIssues, issuesLoading: issuesWidgetLoading } = usePartnerIssues({
  severities: ["High", "Critical"],
  status: "Open",
  take: 5,
});
const { count: openIssueCount } = usePartnerIssueCount();

// Live client summaries (real project + run metrics) — drive both the stat cards and Top Clients.
const { clients: partnerClients, clientsLoading, clientsError, refetch: refetchClients } =
  usePartnerClientSummaries();

const isLoading = computed(() => clientsLoading.value);
const hasError = computed(() => !!clientsError.value);

const partnerStats = computed<PartnerStatsType>(() => {
  const rollup = rollupClientSummaries(partnerClients.value);
  return {
    ...rollup,
    failedRuns: rollup.totalRuns - rollup.successfulRuns,
    criticalIssues: openIssueCount.value,
    recentActivityCount: recentActivityCount.value,
  };
});

const topClients = computed(() =>
  [...partnerClients.value].sort((a, b) => b.totalRuns - a.totalRuns).slice(0, 5)
);
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Partner Dashboard"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Partner' }
        ]"
      >
        <template #actions>
          <router-link to="/partners/clients" class="bb-btn-secondary-cta d-none d-md-inline">
            <i class="bi bi-people me-2"></i>All Clients
          </router-link>
          <router-link to="/partners/settings" class="bb-btn-secondary-cta d-none d-md-inline">
            <i class="bi bi-gear me-2"></i>Partner Settings
          </router-link>
        </template>
      </PageHeader>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        type="general"
        message="Unable to load partner data. Please try again."
        @retry="refetchClients"
      />

      <!-- Content -->
      <div v-else class="row g-4 mt-1">
        <!-- Partner Statistics -->
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Overview Statistics</h2>
              <PartnerStats :stats="partnerStats" :loading="isLoading" />
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="col-12 col-lg-8">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Recent Activity</h2>
              <PartnerActivity
                :activities="recentActivity"
                :loading="isLoading || activityWidgetLoading"
              />
            </div>
          </div>
        </div>

        <!-- Critical Issues -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Issues</h2>
              <IssuesList :issues="criticalIssues" :loading="isLoading || issuesWidgetLoading" />
            </div>
          </div>
        </div>

        <!-- Top Partner Clients -->
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Top Active Clients</h2>
              <PartnerClientsList :clients="topClients" :loading="isLoading" />
            </div>
          </div>
        </div>
      </div>
    </PanelCard>
  </PanelGrid>
</template>

<style scoped lang="scss">
/* Partners dashboard uses standard Bootstrap styling like other pages */
</style>
